import React, { useCallback, useContext, useEffect, useState } from "react";

import {
	Button,
	Chip,
	IconButton,
	makeStyles,
	Menu,
	MenuItem,
	Paper,
	TextField,
	Typography,
} from "@material-ui/core";
import { Add, MoreVert, NotificationsActive, Settings } from "@material-ui/icons";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import ConfirmationModal from "../../components/ConfirmationModal";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { formatDue, formatMoney } from "./format";
import OpportunityDialog from "./OpportunityDialog";
import StagesDialog from "./StagesDialog";

const REFRESH_MS = 60000;

const useStyles = makeStyles(theme => ({
	summary: {
		display: "flex",
		flexWrap: "wrap",
		gap: theme.spacing(1),
		marginBottom: theme.spacing(1),
	},
	alerts: {
		padding: theme.spacing(1.5),
		marginBottom: theme.spacing(1),
		border: `1px solid ${theme.palette.warning ? theme.palette.warning.main : theme.palette.divider}`,
	},
	alertRow: {
		display: "flex",
		flexWrap: "wrap",
		alignItems: "center",
		gap: theme.spacing(1),
		padding: theme.spacing(0.5, 0),
		cursor: "pointer",
	},
	filters: {
		display: "flex",
		flexWrap: "wrap",
		gap: theme.spacing(1),
		marginBottom: theme.spacing(1),
	},
	board: {
		flex: 1,
		display: "flex",
		gap: theme.spacing(1.5),
		overflowX: "auto",
		overflowY: "hidden",
		paddingBottom: theme.spacing(1),
		...theme.scrollbarStyles,
	},
	column: {
		flex: "0 0 280px",
		display: "flex",
		flexDirection: "column",
		minHeight: 0,
		backgroundColor: theme.palette.action.hover,
		[theme.breakpoints.down("xs")]: { flexBasis: "85%" },
	},
	columnOver: {
		outline: `2px dashed ${theme.palette.primary.main}`,
	},
	columnHead: {
		padding: theme.spacing(1, 1.5),
		borderTop: "4px solid",
	},
	cards: {
		flex: 1,
		overflowY: "auto",
		padding: theme.spacing(0, 1, 1),
		...theme.scrollbarStyles,
	},
	card: {
		padding: theme.spacing(1),
		marginBottom: theme.spacing(1),
		cursor: "grab",
	},
	cardTop: {
		display: "flex",
		alignItems: "flex-start",
		justifyContent: "space-between",
	},
	cardTitle: { fontWeight: 600, cursor: "pointer", wordBreak: "break-word" },
	chips: { display: "flex", flexWrap: "wrap", gap: theme.spacing(0.5), marginTop: theme.spacing(0.5) },
	muted: { color: theme.palette.text.secondary },
	empty: { padding: theme.spacing(2), textAlign: "center", color: theme.palette.text.secondary },
	stale: { backgroundColor: "#ed6c02", color: "#fff" },
}));

const Funnel = () => {
	const classes = useStyles();
	const { user } = useContext(AuthContext);
	const isAdmin = user.profile === "admin";

	const [board, setBoard] = useState(null);
	const [alerts, setAlerts] = useState({ overdueTasks: [], staleOpportunities: [], total: 0 });
	const [showAlerts, setShowAlerts] = useState(false);
	const [users, setUsers] = useState([]);
	const [ownerFilter, setOwnerFilter] = useState("");
	const [searchInput, setSearchInput] = useState("");
	const [search, setSearch] = useState("");

	const [dialog, setDialog] = useState({ open: false, opportunity: null });
	const [stagesOpen, setStagesOpen] = useState(false);
	const [menu, setMenu] = useState(null); // { anchor, opportunity }
	const [toDelete, setToDelete] = useState(null);
	const [dragging, setDragging] = useState(null);
	const [over, setOver] = useState(null);

	useEffect(() => {
		const timer = setTimeout(() => setSearch(searchInput.trim()), 400);
		return () => clearTimeout(timer);
	}, [searchInput]);

	useEffect(() => {
		if (!isAdmin) return;
		api
			.get("/users/", { params: { pageNumber: 1 } })
			.then(({ data }) => setUsers(data.users || []))
			.catch(toastError);
	}, [isAdmin]);

	const load = useCallback(async () => {
		try {
			const [b, a] = await Promise.all([
				api.get("/funnel/board", {
					params: { userId: ownerFilter || undefined, search: search || undefined },
				}),
				api.get("/funnel/alerts"),
			]);
			setBoard(b.data);
			setAlerts(a.data);
		} catch (err) {
			toastError(err);
		}
	}, [ownerFilter, search]);

	useEffect(() => {
		void load();
		const timer = setInterval(load, REFRESH_MS);
		return () => clearInterval(timer);
	}, [load]);

	const stages = board ? board.stages : [];
	const summary = board ? board.summary : null;

	const openOpportunity = async id => {
		try {
			const { data } = await api.get(`/funnel/opportunities/${id}`);
			setDialog({ open: true, opportunity: data });
		} catch (err) {
			toastError(err);
		}
	};

	const moveTo = async (opportunityId, stageId) => {
		try {
			await api.post(`/funnel/opportunities/${opportunityId}/move`, { stageId });
			await load();
		} catch (err) {
			toastError(err);
		}
	};

	const confirmDelete = async () => {
		try {
			await api.delete(`/funnel/opportunities/${toDelete.id}`);
			toast.success(i18n.t("funnel.deleted"));
			setToDelete(null);
			await load();
		} catch (err) {
			toastError(err);
		}
	};

	const onDrop = (e, stage) => {
		e.preventDefault();
		setOver(null);
		const id = dragging;
		setDragging(null);
		if (id) void moveTo(id, stage.id);
	};

	return (
		<MainContainer>
			<ConfirmationModal
				title={i18n.t("funnel.confirmDeleteTitle")}
				open={Boolean(toDelete)}
				onClose={() => setToDelete(null)}
				onConfirm={confirmDelete}
			>
				{i18n.t("funnel.confirmDeleteMessage")}
			</ConfirmationModal>
			<OpportunityDialog
				open={dialog.open}
				opportunity={dialog.opportunity}
				stages={stages}
				users={users}
				isAdmin={isAdmin}
				onClose={() => setDialog({ open: false, opportunity: null })}
				onChanged={load}
			/>
			{isAdmin && (
				<StagesDialog open={stagesOpen} onClose={() => setStagesOpen(false)} onChanged={load} />
			)}
			<MainHeader>
				<Title>{i18n.t("funnel.title")}</Title>
				<MainHeaderButtonsWrapper>
					{alerts.total > 0 && (
						<Button
							variant="outlined"
							color="secondary"
							startIcon={<NotificationsActive />}
							onClick={() => setShowAlerts(v => !v)}
						>
							{i18n.t("funnel.alerts.button", { total: alerts.total })}
						</Button>
					)}
					{isAdmin && (
						<Button variant="outlined" startIcon={<Settings />} onClick={() => setStagesOpen(true)}>
							{i18n.t("funnel.stages.button")}
						</Button>
					)}
					<Button
						variant="contained"
						color="primary"
						startIcon={<Add />}
						onClick={() => setDialog({ open: true, opportunity: null })}
					>
						{i18n.t("funnel.new")}
					</Button>
				</MainHeaderButtonsWrapper>
			</MainHeader>

			{summary && (
				<div className={classes.summary}>
					<Chip label={i18n.t("funnel.summary.open", { total: summary.openCount })} />
					<Chip label={i18n.t("funnel.summary.openValue", { total: formatMoney(summary.openValue) })} />
					<Chip label={i18n.t("funnel.summary.won", { total: summary.wonCount })} />
					<Chip label={i18n.t("funnel.summary.wonValue", { total: formatMoney(summary.wonValue) })} />
					<Chip
						label={
							summary.winRate === null
								? i18n.t("funnel.summary.noRate")
								: i18n.t("funnel.summary.rate", { total: summary.winRate })
						}
					/>
				</div>
			)}

			{showAlerts && alerts.total > 0 && (
				<Paper variant="outlined" className={classes.alerts}>
					{alerts.overdueTasks.map(t => (
						<div key={`t${t.id}`} className={classes.alertRow} onClick={() => openOpportunity(t.opportunityId)}>
							<Chip size="small" color="secondary" label={i18n.t("funnel.alerts.taskOverdue")} />
							<Typography variant="body2">
								{t.title} — {t.opportunityTitle}
								{t.contactName ? ` (${t.contactName})` : ""} · {formatDue(t.dueAt)}
							</Typography>
						</div>
					))}
					{alerts.staleOpportunities.map(o => (
						<div key={`o${o.id}`} className={classes.alertRow} onClick={() => openOpportunity(o.id)}>
							<Chip size="small" className={classes.stale} label={i18n.t("funnel.alerts.stale")} />
							<Typography variant="body2">
								{o.title}
								{o.contactName ? ` (${o.contactName})` : ""} ·{" "}
								{i18n.t("funnel.alerts.days", { total: o.daysInStage })}
							</Typography>
						</div>
					))}
				</Paper>
			)}

			<div className={classes.filters}>
				<TextField
					size="small"
					variant="outlined"
					placeholder={i18n.t("funnel.search")}
					value={searchInput}
					onChange={e => setSearchInput(e.target.value)}
					style={{ minWidth: 220 }}
				/>
				{isAdmin && (
					<TextField
						select
						size="small"
						variant="outlined"
						label={i18n.t("funnel.owner")}
						value={ownerFilter}
						onChange={e => setOwnerFilter(e.target.value)}
						style={{ minWidth: 180 }}
					>
						<MenuItem value="">{i18n.t("funnel.allOwners")}</MenuItem>
						<MenuItem value="none">{i18n.t("funnel.noOwner")}</MenuItem>
						{users.map(u => (
							<MenuItem key={u.id} value={u.id}>
								{u.name}
							</MenuItem>
						))}
					</TextField>
				)}
			</div>

			<div className={classes.board}>
				{stages.map(stage => (
					<Paper
						key={stage.id}
						variant="outlined"
						className={`${classes.column} ${over === stage.id ? classes.columnOver : ""}`}
						onDragOver={e => {
							e.preventDefault();
							if (over !== stage.id) setOver(stage.id);
						}}
						onDragLeave={() => setOver(prev => (prev === stage.id ? null : prev))}
						onDrop={e => onDrop(e, stage)}
					>
						<div className={classes.columnHead} style={{ borderTopColor: stage.color }}>
							<Typography variant="subtitle2">
								{stage.name} · {stage.count}
							</Typography>
							<Typography variant="caption" className={classes.muted}>
								{formatMoney(stage.total)}
							</Typography>
						</div>
						<div className={classes.cards}>
							{stage.opportunities.length === 0 && (
								<Typography variant="body2" className={classes.empty}>
									{i18n.t("funnel.emptyStage")}
								</Typography>
							)}
							{stage.opportunities.map(o => (
								<Paper
									key={o.id}
									className={classes.card}
									draggable
									onDragStart={e => {
										e.dataTransfer.setData("text/plain", String(o.id));
										e.dataTransfer.effectAllowed = "move";
										setDragging(o.id);
									}}
									onDragEnd={() => {
										setDragging(null);
										setOver(null);
									}}
								>
									<div className={classes.cardTop}>
										<Typography
											variant="body2"
											className={classes.cardTitle}
											onClick={() => openOpportunity(o.id)}
										>
											{o.title}
										</Typography>
										<IconButton
											size="small"
											aria-label={i18n.t("funnel.menu")}
											onClick={e => setMenu({ anchor: e.currentTarget, opportunity: o })}
										>
											<MoreVert fontSize="small" />
										</IconButton>
									</div>
									<Typography variant="caption" className={classes.muted} display="block">
										{o.contactName}
										{isAdmin && o.userName ? ` · ${o.userName}` : ""}
									</Typography>
									<Typography variant="body2">{formatMoney(o.value)}</Typography>
									<div className={classes.chips}>
										{o.stale && (
											<Chip
												size="small"
												className={classes.stale}
												label={i18n.t("funnel.alerts.days", { total: o.daysInStage })}
											/>
										)}
										{o.overdueTasks > 0 && (
											<Chip
												size="small"
												color="secondary"
												label={i18n.t("funnel.card.overdue", { total: o.overdueTasks })}
											/>
										)}
										{o.overdueTasks === 0 && o.openTasks > 0 && (
											<Chip
												size="small"
												variant="outlined"
												label={i18n.t("funnel.card.tasks", { total: o.openTasks })}
											/>
										)}
									</div>
								</Paper>
							))}
							{stage.truncated && (
								<Typography variant="caption" className={classes.muted}>
									{i18n.t("funnel.truncated", { total: stage.opportunities.length })}
								</Typography>
							)}
						</div>
					</Paper>
				))}
			</div>

			<Menu
				anchorEl={menu && menu.anchor}
				open={Boolean(menu)}
				onClose={() => setMenu(null)}
			>
				{menu &&
					stages
						.filter(s => s.id !== menu.opportunity.stageId)
						.map(s => (
							<MenuItem
								key={s.id}
								onClick={() => {
									const id = menu.opportunity.id;
									setMenu(null);
									void moveTo(id, s.id);
								}}
							>
								{i18n.t("funnel.moveTo", { stage: s.name })}
							</MenuItem>
						))}
				{menu && (
					<MenuItem
						onClick={() => {
							setToDelete(menu.opportunity);
							setMenu(null);
						}}
					>
						{i18n.t("funnel.delete")}
					</MenuItem>
				)}
			</Menu>
		</MainContainer>
	);
};

export default Funnel;
